import React, {useState, useMemo, useEffect, Fragment} from "react"
import {makeStyles} from "@material-ui/core/styles"
import Button from "@material-ui/core/Button"
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import am4themesMaterial from "@amcharts/amcharts4/themes/material"
import am4themesDark from "@amcharts/amcharts4/themes/dark"
import am4themesAnimated from "@amcharts/amcharts4/themes/animated"
import {red, green, blue, orange, grey, pink, teal, brown, purple} from "@material-ui/core/colors"
import {useStore, useDispatch} from "../Store"
import {arrayInit1D, weekendJudge} from "../common"
import {useTranslation} from "react-i18next"
import "../i18n"
import {useOnUpdate, useTheme} from "../hooks"
import {useMediaQuery} from "@material-ui/core"

// am4core.options.autoDispose = true

const useStyles = makeStyles(() => ({
  root: {
    textAlign: `center`,
    margin: `0 auto`,
  },
  button: {
    zIndex: 0,
    margin: `0.5rem`,
    backgroundColor: blue.A700,
    color: `white`,
    "&:hover": {
      backgroundColor: blue.A400,
    },
    width: (props) => (props.isMobile ? `45%` : 250),
  },
}))

export default function CandleChart({id, closing, isDialog}) {
  let forecastSeries = [null, null, null]
  const {t} = useTranslation()
  const {
    store: {
      scale,
      themeColor,
      perspective,
      tabs,
      weightedAverage,
      similarExecFlag,
      maDay,
      locale,
      mainType,
      mainCategory,
      rankingPage,
      inputCompareList,
      mainTerm,
      forwardOption,
      mainTarget,
      mainBaseDate,
      forecastDate,
      mainRiseFallDate,
      subPerspective,
      chartUpdateFlag,
    },
  } = useStore()
  const [nikkeiNews, setNikkeiNews] = useState(``)
  const [googleNews, setGoogleNews] = useState(``)
  const [zoomButtonBox] = useState(arrayInit1D(4, null))
  const NIKKEI_NEWS_URL = process.env.NIKKEI_NEWS_URL
  const GOOGLE_NEWS_URL = process.env.GOOGLE_NEWS_URL
  const theme = useTheme(themeColor)
  const isMobile = useMediaQuery(theme.breakpoints.down(`xs`))
  const classes = useStyles({
    isMobile,
  })
  const {dispatch} = useDispatch()
  const localRankingPage = isDialog ? rankingPage : 0
  const [chartData, setChartData] = useState([null, null, null])
  const [dateAxisData] = useState([null, null, null])
  const isFx = mainType[perspective] === `FX` || mainType[perspective] === `FXSET`

  const disposeChart = () => {
    if (chartData[perspective] && !chartData[perspective].isDisposed()) chartData[perspective].dispose()
    chartData[perspective] = null
    setChartData(chartData)
  }

  const createChart = (priceList = null) => {
    const chart = am4core.create(id, am4charts.XYChart)
    chart.showOnInit = false
    if (themeColor === `dark`) am4core.useTheme(am4themesDark)
    if (themeColor !== `dark`) am4core.useTheme(am4themesMaterial)
    am4core.useTheme(am4themesAnimated)
    if (priceList) chart.data = forwardOption[perspective] ? priceList.backward.concat(priceList.forward) : priceList.backward
    chart.cursor = new am4charts.XYCursor()
    chart.cursor.fill = am4core.color(theme.palette.primary.contrastText)
    chart.cursor.behavior = isMobile ? `none` : `zoomX`
    chart.leftAxesContainer.layout = `vertical`
    chart.svgContainer.htmlElement.style.height = scale
    chart.svgContainer.htmlElement.style.width = `100%`
    chart.paddingLeft = isMobile ? 15 : 50
    chart.paddingRight = isMobile ? 15 : 50
    chart.zoomOutButton.marginTop = 0
    chart.zoomOutButton.marginRight = 5
    chart.zoomOutButton.marginBottom = 10
    chart.zoomOutButton.background.cornerRadius(5, 5, 5, 5)
    chart.zoomOutButton.background.fill = am4core.color(blue.A700)
    chart.zoomOutButton.icon.height = 10
    chart.zoomOutButton.icon.stroke = am4core.color(grey[50])
    chart.zoomOutButton.icon.strokeWidth = 2
    chart.zoomOutButton.background.states.getKey(`hover`).properties.fill = am4core.color(blue.A400)
    // chart.exporting.menu = new am4core.ExportMenu()
    const zoomButton = chart.plotContainer.createChild(am4core.Container)
    zoomButton.shouldClone = false
    zoomButton.align = `right`
    zoomButton.valign = `top`
    zoomButton.zIndex = Number.MAX_SAFE_INTEGER
    zoomButton.marginTop = 0
    zoomButton.marginRight = 40
    zoomButton.marginBottom = 0
    zoomButton.layout = `horizontal`
    createZoomChildButton(zoomButton, 0, t(`scaleUpMobile`), t(`scaleUp`))
    createZoomChildButton(zoomButton, 1, t(`scaleDownMobile`), t(`scaleDown`))
    return chart
  }

  const createDateAxis = (inputData = null, chart) => {
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis())
    dateAxis.groupData = !isFx
    dateAxis.minZoomCount = 5
    dateAxis.skipEmptyPeriods = inputData ? weekendJudge(inputData.term) : true
    dateAxis.renderer.inside = false
    dateAxis.renderer.grid.template.strokeWidth = 1
    dateAxis.renderer.grid.template.strokeOpacity = 1
    dateAxis.dateFormats.setKey(`day`, `yyyy-MM-dd`)
    dateAxis.dateFormats.setKey(`month`, `yyyy-MM`)
    dateAxis.periodChangeDateFormats.setKey(`month`, `[bold]yyyy`)
    dateAxis.title.fill = am4core.color(orange.A700)
    dateAxis.title.fontWeight = `bold`
    dateAxis.title.marginTop = 10
    dateAxis.adapter.add(`getTooltipText`, () => {
      return null
    })
    return dateAxis
  }

  const createValueAxis = (inputData = null, chart, kind = `VOLUME`) => {
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
    if (isMobile) valueAxis.renderer.labels.template.verticalCenter = `bottom`
    if (isMobile) valueAxis.renderer.labels.template.padding(2, 2, 2, 2)
    valueAxis.renderer.inside = true
    valueAxis.extraMin = 0.1
    valueAxis.extraMax = 0.1
    valueAxis.renderer.labels.template.dy = -15
    valueAxis.renderer.grid.template.strokeWidth = 1
    valueAxis.renderer.grid.template.strokeOpacity = 1
    valueAxis.renderer.labels.template.fill = am4core.color(theme.palette.primary.contrastText)
    valueAxis.renderer.gridContainer.background.fill = am4core.color(theme.palette.background.default)
    valueAxis.height = am4core.percent(100)
    valueAxis.height = inputData
      ? isFx
        ? am4core.percent(0)
        : kind === `VOLUME`
        ? am4core.percent(50)
        : am4core.percent(100)
      : am4core.percent(100)
    valueAxis.marginTop = kind === `VOLUME` ? 50 : 40
    valueAxis.adapter.add(`getTooltipText`, () => {
      return null
    })
    return valueAxis
  }

  const createCandleSeries = (inputData = null, chart) => {
    const date = chart?.data[0]?.date ?? null
    const open = chart?.data[0]?.open ?? null
    const high = chart?.data[0]?.high ?? null
    const low = chart?.data[0]?.low ?? null
    const close = chart?.data[0]?.close ?? null
    if (!date || !open || !low || !high || !close) return null
    const candleSeries = chart.series.push(new am4charts.CandlestickSeries())
    candleSeries.showOnInit = false
    candleSeries.name = inputData ? (isFx ? `${t(`exchangeRate`)}` : `${t(`equityPrice`)}`) : ``
    candleSeries.dataFields.dateX = `date`
    candleSeries.dataFields.valueY = `close`
    candleSeries.dataFields.openValueY = `open`
    candleSeries.dataFields.lowValueY = `low`
    candleSeries.dataFields.highValueY = `high`
    const itemName = inputData ? (isFx ? t(`exchangeRate`) : t(`equityPrice`)) : ``
    candleSeries.tooltipText =
      `[font-size:16]{dateX.formatDate('yyyy-MM-dd')}\n${itemName}\n` +
      `${t(`open`)}  : {openValueY.value}\n` +
      `${t(`low`)}   : {lowValueY.value}\n` +
      `${t(`high`)}  : {highValueY.value}\n` +
      `${t(`close`)} : {valueY.value}`
    candleSeries.fill = am4core.color(blue.A700)
    candleSeries.tooltip.autoTextColor = false
    candleSeries.tooltip.label.fill = am4core.color(grey[50])
    candleSeries.legendSettings.labelText = `[bold white]{name}[/]`
    candleSeries.dropFromOpenState.properties.fill = am4core.color(green.A700)
    candleSeries.dropFromOpenState.properties.stroke = am4core.color(green.A700)
    candleSeries.riseFromOpenState.properties.fill = am4core.color(red[500])
    candleSeries.riseFromOpenState.properties.stroke = am4core.color(red[500])
    const scrollbarX = new am4charts.XYChartScrollbar()
    scrollbarX.series.push(candleSeries)
    scrollbarX.background.fill = am4core.color(theme.palette.background.default)
    scrollbarX.background.fillOpacity = 1
    chart.scrollbarX = scrollbarX
    return candleSeries
  }

  const createColumnSeries = (chart, dateAxis, valueAxis) => {
    const date = chart?.data[0]?.date ?? null
    const volume = chart?.data[0]?.volume ?? null
    if (!date || !volume) return null
    const columnSeries = chart.series.push(new am4charts.ColumnSeries())
    columnSeries.showOnInit = false
    columnSeries.name = t(`volume`)
    columnSeries.dataFields.dateX = `date`
    columnSeries.dataFields.valueY = `volume`
    columnSeries.xAxis = dateAxis
    columnSeries.yAxis = valueAxis
    columnSeries.strokeWidth = 0
    columnSeries.tooltipText = `[font-size:16]{dateX.formatDate('yyyy-MM-dd')}\n{name}\n{valueY}[/]`
    columnSeries.fill = am4core.color(blue.A700)
    columnSeries.legendSettings.labelText = `[bold white]{name}[/]`
    return columnSeries
  }

  const createMALineSeries = (chart, valueAxis, field, name, color, bulletType = ``) => {
    const {date = null, movement0 = null, movement1 = null, movement2 = null} = chart.data[0]
    if (!date || !movement0 || !movement1 || !movement2) return null
    const maLineSeries = chart.series.push(new am4charts.LineSeries())
    maLineSeries.showOnInit = false
    maLineSeries.dataFields.dateX = `date`
    maLineSeries.dataFields.valueY = field
    maLineSeries.stroke = am4core.color(color)
    maLineSeries.fill = am4core.color(color)
    maLineSeries.strokeWidth = 3
    maLineSeries.yAxis = valueAxis
    maLineSeries.name = name
    maLineSeries.tooltipText = `{name}: [bold]{valueY}[/]`
    maLineSeries.tensionX = 0.8
    if (bulletType === `triangle`) {
      const bullet = maLineSeries.bullets.push(new am4charts.Bullet())
      bullet.width = 12
      bullet.height = 12
      bullet.horizontalCenter = `middle`
      bullet.verticalCenter = `middle`
      const triangle = bullet.createChild(am4core.Triangle)
      triangle.strokeWidth = 2
      triangle.direction = `top`
      triangle.width = 12
      triangle.height = 12
    }
    if (bulletType === `rectangle`) {
      const bullet = maLineSeries.bullets.push(new am4charts.Bullet())
      bullet.width = 10
      bullet.height = 10
      bullet.horizontalCenter = `middle`
      bullet.verticalCenter = `middle`
      const rectangle = bullet.createChild(am4core.Rectangle)
      rectangle.strokeWidth = 2
      rectangle.width = 10
      rectangle.height = 10
    }
    if (bulletType === `circle`) {
      const bullet = maLineSeries.bullets.push(new am4charts.CircleBullet())
      bullet.circle.strokeWidth = 2
    }
  }

  const createLineSeries = (inputData = null, chart) => {
    // FXデータで４本値データがない場合は、ラインチャートで表示
    const date = chart?.data[0]?.date ?? null
    const middle = chart?.data[0]?.middle ?? null
    if (!date || !middle) return null
    const lineSeries = chart.series.push(new am4charts.LineSeries())
    lineSeries.showOnInit = false
    lineSeries.dataFields.dateX = `date`
    lineSeries.dataFields.valueY = `middle`
    const itemName = inputData ? (isFx ? t(`exchangeRate`) : t(`equityPrice`)) : ``
    lineSeries.tooltipText = `${itemName}: {valueY.value}`
    lineSeries.tooltip.autoTextColor = false
    lineSeries.tooltip.label.fill = am4core.color(grey[50])
    lineSeries.strokeWidth = 2
    lineSeries.propertyFields.stroke = `color`
    return lineSeries
  }

  const createPointSeries = (chart, data, itemName, color) => {
    const lineSeries = chart.series.push(new am4charts.LineSeries())
    lineSeries.showOnInit = false
    lineSeries.dataFields.dateX = `date`
    lineSeries.dataFields.valueY = `value`
    //lineSeries.tooltipText = `[bold font-size: 18px]${itemName}\n{date}[/]`
    lineSeries.tooltipText = `{date}\n[bold font-size: 18px]${itemName}\n[font-size: 16px]\n{value}`
    lineSeries.tooltip.autoTextColor = false
    lineSeries.tooltip.label.fill = am4core.color(grey[50])
    lineSeries.strokeWidth = 10
    lineSeries.fill = am4core.color(color)
    lineSeries.data = data
    return lineSeries
  }

  const createCircleBullet = (lineSeries, color) => {
    const circleBullet = lineSeries.bullets.push(new am4charts.CircleBullet())
    circleBullet.circle.radius = 10
    circleBullet.strokeWidth = 5
    circleBullet.stroke = am4core.color(color)
    const hoverState = circleBullet.states.create(`hover`)
    hoverState.properties.scale = 1.5
    return circleBullet
  }

  const createBasePoint = (priceData) => {
    const {date = null, close = null, middle = null} = priceData
    const value = close || middle || null
    return [
      {
        date,
        value,
      },
      {
        date,
        value,
      },
    ]
  }

  const createZoomChildButton = (zoomButton, kind, label1, label2) => {
    const button = zoomButton.createChild(am4core.Button)
    button.label.text = isMobile ? label1 : label2
    button.label.fill = am4core.color(grey[50])
    button.label.strokeWidth = 4
    button.label.valign = "middle"
    button.marginTop = 0
    button.marginRight = 10
    button.marginBottom = 0
    button.width = isMobile ? 60 : 150
    button.height = 35
    button.textAlign = `center`
    button.strokeWidth = 0
    button.background.cornerRadius(5, 5, 5, 5)
    button.background.fill = am4core.color(pink.A400)
    button.background.states.getKey(`hover`).properties.fill = am4core.color(pink.A200)
    zoomButtonBox[kind] = button
  }

  // const handleZoom = (zoomFlag = `in`, dateAxis) => {
  //   const {min, minZoomed, maxZoomed, max} = dateAxis
  //   const diff = maxZoomed - minZoomed
  //   if (zoomFlag === `in`) {
  //     const delta = diff * 0.2
  //     dateAxis.zoomToDates(new Date(minZoomed + delta), new Date(maxZoomed - delta))
  //   } else {
  //     const nextMin = (minZoomed - diff < min) ? min : maxZoomed - diff*2.5
  //     const nextMax = (maxZoomed + diff > max) ? max : maxZoomed + diff
  //     dateAxis.zoomToDates(new Date(nextMin), new Date(nextMax))
  //   }
  // }

  const changeScale = (kind, chart) => {
    const mainHeight = Number(chart.svgContainer.htmlElement.style.height.replace(`px`, ``))
    const nextHeight = kind === `up` ? mainHeight + 100 : mainHeight - 100 >= 400 ? mainHeight - 100 : 400
    chart.svgContainer.htmlElement.style.height = `${nextHeight}px`
  }

  const getForecastDate = (priceList) => {
    if (mainRiseFallDate[perspective] <= priceList.forwardNorm[subPerspective[perspective]].length) {
      forecastDate[perspective] = priceList.forwardNorm[subPerspective[perspective]][mainRiseFallDate[perspective] - 1].date
    } else {
      forecastDate[perspective] = priceList.forwardNorm[subPerspective[perspective]].slice(-1)[0].date
    }
    dispatch({
      type: `SET_FORECAST_DATE`,
      forecastDate,
    })
    return forecastDate
  }

  const addBaseRiseFallPoint = () => {
    const {inputData = null, priceList = null} = inputCompareList[perspective][localRankingPage]
    if (!inputData || !priceList) return
    const {backward = null} = priceList
    if (!backward) return
    const baseDatePrice = priceList.backward.slice(-1)[0]
    let {close = null, middle = null} = baseDatePrice
    const kind = 0 // 基準日
    let targetPoint = createBasePoint(baseDatePrice)
    forecastSeries[kind] = createPointSeries(chartData[perspective], targetPoint, t(`baseDate`), orange.A700)
    createCircleBullet(forecastSeries[kind], orange.A200)
    if (forwardOption[perspective]) {
      let riseFallDatePrice = baseDatePrice
      if (inputData.riseFallDate <= priceList.forward.length) {
        riseFallDatePrice = priceList.forward[inputData.riseFallDate - 1]
      } else if (priceList.forward.length) {
        riseFallDatePrice = priceList.forward.slice(-1)[0]
      }
      if (baseDatePrice.date !== riseFallDatePrice.date) {
        const kind = 1 // 騰落日: 実績値
        riseFallDatePrice = {
          ...riseFallDatePrice,
          date: riseFallDatePrice.date,
        }
        targetPoint = createBasePoint(riseFallDatePrice)
        //forecastSeries[kind] = createPointSeries(chartData[perspective], targetPoint, t(`riseFallDay`), blue.A700)    // 「騰落日」→「予測日」
        forecastSeries[kind] = createPointSeries(chartData[perspective], targetPoint, t(`forecastDate`), blue.A700)
        forecastSeries[kind].data = targetPoint
        createCircleBullet(forecastSeries[kind], blue.A200)
      }
      if (localRankingPage === 0) {
        const kind = 2 // 予測日: 加重平均値（予測値）
        const forecastDate = getForecastDate(priceList)
        if (weightedAverage[perspective]) {
          if (close) close = Math.round((weightedAverage[perspective] / 100.0 + 1) * close)
          if (middle) middle = (weightedAverage[perspective] / 100.0 + 1) * middle
          const weightedAveragePrice = {
            date: forecastDate[perspective],
            close,
            middle,
          }
          const targetPoint = createBasePoint(weightedAveragePrice)
          forecastSeries[kind] = createPointSeries(chartData[perspective], targetPoint, t(`weightedAverageValue`), pink.A400)
          forecastSeries[kind].data = targetPoint
          createCircleBullet(forecastSeries[kind], pink.A200)
          // Nakamura Memo : なぜポップアップが表示されるのかよくわからないので、表示されるのを防止（チャートのツールチップが表示される）
          //if (forecastSeries[kind]) {
          //  forecastSeries[kind].events.on(`tooltipshownat`, () => {
          //    forecastResult(weightedAverage[perspective], forecastDate[perspective])
          //  })
          //}
        }
      }
    }
  }

  const forecastResult = (weightedAverage, forecastDate) => {
    const title = t(`forecastTitle`)
    const message = `${t(`baseDate`)}: ${mainBaseDate[perspective].key}`
    // const message = `${t(`baseDate`)}: ${mainBaseDate[perspective].key}\n${t(`forecastDate`)}: ${forecastDate}`
    let dayFlag = false
    let business = ``
    // if (mainBaseDate[perspective].key.includes(`D`)) {
    //   business = t(`forecast.businessDay`)
    //   dayFlag = true
    // }
    if (mainTerm[perspective].key.includes(`D`)) business = t(`forecast.businessDay`)
    if (mainTerm[perspective].key.includes(`W`)) business = t(`forecast.businessWeek`)
    if (mainTerm[perspective].key.includes(`M`)) business = t(`forecast.businessMonth`)
    if (mainTerm[perspective].key.includes(`Q`)) business = t(`forecast.businessQuarter`)
    const upDown = weightedAverage > 0 ? t(`forecast.up`) : t(`forecast.down`)
    const result = weightedAverage > 0 ? true : false
    const content = t(`forecast.comment`, {
      message1: mainRiseFallDate[perspective],
      message2: business,
      message3: t(`forecast.after`),
      message4: weightedAverage,
      message5: upDown,
      message6: `(${forecastDate})`,
      joinArrays: ` `,
    })
    const confirmDialog = {
      title,
      message,
      content,
      result,
      state: true,
    }
    dispatch({
      type: `CONFIRM_DIALOG`,
      confirmDialog,
    })
  }

  /** 丸めた数値を返す */
  const getRound = (v, n = 2) => {
    return Math.round(v * (10 ** n)) / (10 ** n)
  }

  const riseFall = (priceList = []) => {
    if (priceList.length === 0) return
    if (priceList.forward.length === 0) return `0.00`
    if (mainRiseFallDate[perspective] === 0) return 0
    let baseDateValue = 1.0
    if (isFx) {
      const {middle = null, close = null} = (baseDateValue = priceList.backward.slice(-1)[0])
      if (middle) baseDateValue = middle
      if (close) baseDateValue = close
      if (!middle && !close) baseDateValue = 1.0
    } else {
      const {close = 1.0} = (baseDateValue = priceList.backward.slice(-1)[0])
      baseDateValue = close
    }
    const judge = mainRiseFallDate[perspective] <= priceList.forward.length ? true : false
    let riseFallDateValue = 1.0
    if (judge) {
      if (isFx) {
        const {middle = null, close = null} = priceList.forward[mainRiseFallDate[perspective] - 1]
        if (middle) riseFallDateValue = middle
        if (close) riseFallDateValue = close
        if (!middle && !close) riseFallDateValue = 1.0
      } else {
        const {close = 1.0} = priceList.forward[mainRiseFallDate[perspective] - 1]
        riseFallDateValue = close
      }
    } else {
      if (isFx) {
        const {middle = null, close = null} = priceList.forward.slice(-1)[0]
        if (middle) riseFallDateValue = middle
        if (close) riseFallDateValue = close
        if (!middle && !close) riseFallDateValue = 1.0
      } else {
        const {close = 1.0} = priceList.forward.slice(-1)[0]
        riseFallDateValue = close
      }
    }
    
    return 100 * (riseFallDateValue - baseDateValue) / baseDateValue
  }

  /** メイン銘柄情報を表示する */
  const forecastEffect = () => {
    if (!(inputCompareList?.[perspective]?.[localRankingPage] ?? null)) return
    if (!mainTarget[perspective].key) return
    if (!mainTerm[perspective].key) return
    if (!mainBaseDate[perspective].key) return

    const {inputData, priceList = null} = inputCompareList[perspective][localRankingPage]
    const estimateRate = getRound(weightedAverage[perspective], 2)
    const riseFallRate = priceList ? getRound(riseFall(priceList)) : `----`
    const {baseDate = null} = inputData

    let title = `${t(`riseFallWeightedAverageRate`)}: ${estimateRate}%`
    title += isMobile ? `\n` : ` | `
    title += `${t(`riseFallRate`)}: ${riseFallRate}%`
    title += isMobile ? `\n` : ` | `
    title += `${t(`baseDate`)}: ${baseDate}`
    title += isMobile ? `\n` : ` | `
    if (isFx) {
      title += ` ${t(`targetFx`)}: ${mainTarget[perspective].key}`
    } else {
      title += t(`equityName`)
      title += `: ${mainTarget[perspective].label}`
      if (mainCategory[perspective] !== `none`) {
        title += isMobile ? `\n` : ` | `
        title += t(`${mainCategory[perspective]}Name`) /* i18next-extract-disable-line */
        title +=
          locale === `ja`
            ? `: ${inputCompareList[perspective][localRankingPage].inputData.categoryNameJa}`
            : `: ${inputCompareList[perspective][localRankingPage].inputData.categoryNameEn}`
      }
    }
    if (dateAxisData[perspective]) dateAxisData[perspective].title.text = title
    const forecastDate = getForecastDate(priceList)
    forecastResult(weightedAverage[perspective], forecastDate[perspective])
    dispatch({
      type: `SET_FORECAST_DATE`,
      forecastDate,
    })
    dispatch({
      type: `SWITCH_PAGE_FLAG`,
      switchPageFlag: false,
    })
  }
  useOnUpdate(forecastEffect, Object.values(similarExecFlag).map((v) => {return v}))

  const forwardOptionEffect = () => {
    if (!chartData[perspective] || chartData[perspective].isDisposed()) return
    if (!inputCompareList[perspective][localRankingPage]) return
    const priceList = inputCompareList[perspective][localRankingPage]?.priceList ?? null
    if (!priceList) return
    const fetch = async () => {
      await new Promise((resolve) => setTimeout(resolve, 500))
      if (forwardOption[perspective] && priceList.forward.length) {
        chartData[perspective].data = priceList.backward.concat(priceList.forward)
      } else {
        chartData[perspective].data = priceList.backward
      }
      addBaseRiseFallPoint()
      setChartData(chartData)
    }
    fetch()
  }
  useOnUpdate(forwardOptionEffect, Object.values(forwardOption).map((v) => {return v}))

  const createCandleChart = () => {
    let inputData = null
    let priceList = null
    if (!inputCompareList[perspective]) return
    if (inputCompareList[perspective][localRankingPage]) {
      inputData = inputCompareList[perspective][localRankingPage]?.inputData
      priceList = inputCompareList[perspective][localRankingPage]?.priceList
    }
    if (!priceList) return
    disposeChart()
    chartData[perspective] = createChart(priceList)
    if (zoomButtonBox) {
      zoomButtonBox[0].events.on(`hit`, () => changeScale(`up`, chartData[perspective]))
      zoomButtonBox[1].events.on(`hit`, () => changeScale(`down`, chartData[perspective]))
    }
    dateAxisData[perspective] = createDateAxis(inputData, chartData[perspective])
    const candleValueAxis = createValueAxis(inputData, chartData[perspective], `CANDLE`)
    createCandleSeries(inputData, chartData[perspective])
    createLineSeries(inputData, chartData[perspective])
    const valueAxis = createValueAxis(inputData, chartData[perspective], `VOLUME`)
    createColumnSeries(chartData[perspective], dateAxisData[perspective], valueAxis)
    // eslint-disable-next-line no-unused-vars
    const _ = Object.values(tabs).map((_, idx) => {
      const lineColor = idx === 0 ? purple[500] : idx === 1 ? brown[500] : teal[500]
      createMALineSeries(
        chartData[perspective],
        candleValueAxis,
        `movement${idx}`,
        `${t(`mal`)}${maDay[perspective][idx]}${t(`days`)}`,
        lineColor,
      )
    })

    const similarity = getRound(100 * inputData.similarity, 3)
    const {riseFallRate = `----`} = priceList
    const {baseDate, compareStockNameJa, compareStockNameEn, code, categoryNameJa, categoryNameEn} = inputData
    let title = ``

    if (localRankingPage) {
      title = `${t(`ranking`)}: ${t(`rankingPrefix`)}${localRankingPage}${t(`rankingSuffix`)}`
      title += isMobile ? `\n` : ` | `
      title += `${t(`similarity`)}: ${similarity}%`
      title += isMobile ? `\n` : ` | `
    } else {
      title = `${t(`riseFallWeightedAverageRate`)}: ${weightedAverage[perspective]}%`
      title += isMobile ? `\n` : ` | `
    }
    title += `${t(`riseFallRate`)}: ${getRound(riseFallRate)}%`
    title += isMobile ? `\n` : ` | `
    title += `${t(`baseDate`)}: ${baseDate}`
    title += isMobile ? `\n` : ` | `
    if (isFx) {
      title += `${t(`targetFx`)}: ${code}`
    } else {
      title += t(`equityName`)
      title += locale === `ja` ? `: ${compareStockNameJa}` : `: ${compareStockNameEn}`
      if (mainCategory[perspective] !== `none`) {
        title += isMobile ? `\n` : ` | `
        title += t(`${mainCategory[perspective]}Name`) /* i18next-extract-disable-line */
        title += locale === `ja` ? `: ${categoryNameJa}` : `: ${categoryNameEn}`
      }
    }
    dateAxisData[perspective].title.text = title

    if (!isFx) {
      let searchWord = ``
      if (locale === `ja`) {
        if (compareStockNameJa) {
          let keyword = compareStockNameJa.replace(`（株）`, ``).split(` | `)
          keyword.shift()
          searchWord = keyword.join(` | `)
        }
      }
      if (locale !== `ja`) searchWord = compareStockNameEn
      const searchDate = baseDate.split(`-`).map((date, index) => {
        const intDate = Number(date)
        return index === 0 ? `${intDate}${t(`year`)} ` : index === 1 ? `${intDate}${t(`month`)} ` : `${intDate}${t(`day`)}`
      })
      const nikkeiNews = `${NIKKEI_NEWS_URL}${searchWord}+${searchDate.join(``)}`
      const googleNews = `${GOOGLE_NEWS_URL}${searchWord}+${searchDate.join(``)}`
      setNikkeiNews(nikkeiNews)
      setGoogleNews(googleNews)
    }
    if (!localRankingPage) {
      addBaseRiseFallPoint(`ready`)
    } else {
      addBaseRiseFallPoint()
    }
    setChartData(chartData)
  }

  useEffect(() => {
    createCandleChart()
  }, [chartUpdateFlag, localRankingPage, locale])

  useEffect(() => {
    if (closing ?? null) disposeChart()
  }, [closing])

  const linkButton = useMemo(() => {
    if (isFx) return null
    if (!chartData[perspective]) return null
    return (
      <Fragment>
        <Button className={classes.button} href={nikkeiNews} target="_blank">
          {t(`nikkeiNews`)}
        </Button>
        <Button className={classes.button} href={googleNews} target="_blank">
          {t(`googleNews`)}
        </Button>
      </Fragment>
    )
  }, [classes, chartData[perspective], nikkeiNews, googleNews])

  return (
    <div className={classes.root}>
      <div id={id} />
      {linkButton}
    </div>
  )
}
